'use client'
import type { PartialMessage } from '@bufbuild/protobuf'
import { useQuery } from '@connectrpc/connect-query'
import { getBlogPostsPublic } from '@fingertip/creator-proto/gen/fingertip/creator/blog_post/v1/blog_post-BlogPostService_connectquery'
import type { GetPagePublicResponse } from '@fingertip/creator-proto/gen/fingertip/creator/page/v1/page_pb'
import { ArrowRightIcon, EditBigIcon } from '@fingertip/icons'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'

import { BlockAssetInner } from '@/components/blocks/shared/block-asset-inner'
import { PageFooter } from '@/components/page-footer/page-footer'
import { FooterCta } from '@/components/page-footer/shared/footer-cta'
import { PageHeader } from '@/components/page-header/page-header'
import { PageBackground } from '@/components/public-page/page-background'
import { PageFont } from '@/components/public-page/page-font'
import { PageStyle } from '@/components/public-page/page-style'
import { PageView } from '@/components/public-page/page-view'
import { AlertBanner } from '@/components/shared/alert-banner'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { PHProvider } from '@/lib/posthog/providers'

import { PageLoading } from '../public-page/page-loading'
import { PageNavBar } from '../public-page/page-navbar'
import { PageWhiteShadow } from '../public-page/page-white-shadow'
import { ScaleFactor } from '../public-page/scale-factor'
import { PageMeta } from '../shared/page-meta'

type Props = {
  siteSlug: string
  data: PartialMessage<GetPagePublicResponse>
  domain?: string
}

export const PublicBlogPostsPage = ({ siteSlug, domain, data }: Props) => {
  const { t } = useTranslation()

  const {
    data: postsData,
    error: postsError,
    isLoading: postsIsLoading,
  } = useQuery(getBlogPostsPublic, {
    siteSlug,
  })

  if (postsError) {
    return <AlertBanner>{postsError.message}</AlertBanner>
  }

  if (postsIsLoading) {
    return <PageLoading />
  }

  return (
    <>
      {data?.site?.customHtmlBodyStart && (
        <div
          dangerouslySetInnerHTML={{ __html: data.site.customHtmlBodyStart }}
        />
      )}
      <PageBackground content={data?.pageTheme?.content} />

      <div className="relative flex h-full flex-col">
        <PageStyle content={data?.pageTheme?.content} />
        <PageFont content={data?.pageTheme?.content} />
        <ScaleFactor />

        <div className="relative z-1 w-full flex-1 font-page-body font-page-body-weight text-page-text">
          <div className="relative z-30 mx-auto w-full max-w-[calc(480px*var(--scale-factor))] lg:my-fluid-16">
            <PageHeader
              page={data?.page}
              hasVerifiedBadge={!!data?.site?.verified}
              headerSlug={data?.pageTheme?.content?.headerSlug}
              headerImageStyle={data?.pageTheme?.content?.headerImageStyle}
            />

            <div className="px-fluid-4">
              <div className="mb-fluid-4">
                <div className="ft-widget-wrapper size-full rounded-page-widget text-neutral-900">
                  <div className="bg-page-widget-background shadow-page-widget border-page-widget p-block size-full rounded-page-widget text-page-body-text backdrop-blur-page-widget">
                    <Breadcrumb>
                      <BreadcrumbList>
                        <BreadcrumbItem>
                          <BreadcrumbLink
                            href={domain ? '/' : `/${siteSlug}`}
                            className="text-page-body-text hover:text-page-body-text"
                          >
                            {data?.site?.name || siteSlug}
                          </BreadcrumbLink>
                        </BreadcrumbItem>

                        <BreadcrumbSeparator className="text-page-body-text" />

                        <BreadcrumbItem>
                          <BreadcrumbPage className="text-page-body-text">
                            {t('blog')}
                          </BreadcrumbPage>
                        </BreadcrumbItem>
                      </BreadcrumbList>
                    </Breadcrumb>
                  </div>
                </div>
              </div>

              <div className="flex flex-col space-y-fluid-4">
                {(postsData?.posts || []).map((post, index) => {
                  return (
                    <Link
                      href={`${domain ? '/' : `/${siteSlug}/`}blog/${post?.slug}`}
                      className="relative flex size-full shrink-0 text-left"
                      key={post?.id || index}
                    >
                      <div className="ft-widget-wrapper size-full rounded-page-widget text-neutral-900">
                        <div className="bg-page-widget-background shadow-page-widget border-page-widget size-full rounded-page-widget text-page-body-text backdrop-blur-page-widget">
                          <BlockAssetInner
                            media={post?.featureImage}
                            title={post?.title}
                            description={post?.excerpt}
                            buttonText={t('read_more')}
                            w={30}
                            h={30}
                            y={30}
                            icon={<EditBigIcon className="size-fluid-4" />}
                            buttonIcon={
                              <ArrowRightIcon className="size-fluid-4 shrink-0" />
                            }
                            scaleFactor={1}
                          />
                        </div>
                      </div>
                    </Link>
                  )
                })}
              </div>
            </div>

            <div className="pt-fluid-8">
              <PageFooter
                page={data?.page}
                footerSlug={data?.pageTheme?.content?.footerSlug}
                isWhiteLabeled={data?.site?.isWhiteLabeled}
                siteSlug={siteSlug}
                domain={domain}
              >
                <div className="p-block flex justify-center pt-fluid-8!">
                  <FooterCta siteSlug={siteSlug} />
                </div>
              </PageFooter>
            </div>
          </div>

          <PageWhiteShadow />
        </div>
      </div>

      <PHProvider>
        <PageView
          data={{
            page_slug: 'index',
            page_id: data?.page?.id,
            site_slug: siteSlug,
            site_id: data?.site?.id,
          }}
        />
      </PHProvider>

      <PageMeta site={data?.site} />

      <PageNavBar
        siteSlug={siteSlug}
        pageSlug="index"
        isWhiteLabeled={data.site?.isWhiteLabeled}
      />
    </>
  )
}
